import { useEffect, useRef } from 'react';
import seedrandom from 'seedrandom';
import {
  stepConway,
  seedRandomFill,
  stampGlider,
  stampPulsar,
  stampLWSS,
  stampGosperGun,
  countLive,
} from '../lib/conway';

type Options = {
  cellSize?: number;
  mobileCellSize?: number;
  tickMs?: number;
  mobileTickMs?: number;
  density?: number;
  opacity?: number;
};

export function useGameOfLife(
  canvasRef: React.RefObject<HTMLCanvasElement>,
  options: Options = {}
) {
  const optsRef = useRef(options);
  optsRef.current = options;

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext('2d', { alpha: true });
    if (!ctx) return;

    let cols = 0;
    let rows = 0;
    let cellSize = 8;
    let cells = new Uint8Array(0);
    let next = new Uint8Array(0);
    const rand = seedrandom('automaton-bg');
    let mobile = false;
    let pointer: { x: number; y: number } | null = null;
    let antiStagnationTimer = 0;
    let lastTick = 0;
    let rafId = 0;
    let stopped = false;

    const c = canvas;
    const g = ctx;

    function resize() {
      const dpr = Math.min(window.devicePixelRatio || 1, 2);
      mobile = window.innerWidth < 768;
      const desktopSize = optsRef.current.cellSize ?? 8;
      const mobileSize = optsRef.current.mobileCellSize ?? 12;
      cellSize = mobile ? mobileSize : desktopSize;
      const w = window.innerWidth;
      const h = window.innerHeight;
      c.width = w * dpr;
      c.height = h * dpr;
      c.style.width = w + 'px';
      c.style.height = h + 'px';
      g.setTransform(dpr, 0, 0, dpr, 0, 0);
      cols = Math.ceil(w / cellSize);
      rows = Math.ceil(h / cellSize);
      const size = cols * rows;
      cells = new Uint8Array(size);
      next = new Uint8Array(size);
      seedRandomFill(cells, cols, rows, optsRef.current.density ?? 0.2, rand);
      draw(true);
    }

    function draw(full = false) {
      const w = c.clientWidth;
      const h = c.clientHeight;
      if (full) g.clearRect(0, 0, w, h);
      else g.clearRect(0, 0, w, h);
      g.globalCompositeOperation = 'lighter';
      g.fillStyle = `rgba(30, 111, 255, 0.08)`;
      for (let y = 0; y < rows; y++) {
        for (let x = 0; x < cols; x++) {
          if (cells[y * cols + x]) {
            g.fillRect(x * cellSize, y * cellSize, cellSize - 1, cellSize - 1);
          }
        }
      }
      g.globalCompositeOperation = 'source-over';
    }

    function loop(now: number) {
      if (stopped) return;
      const tickMs = mobile
        ? optsRef.current.mobileTickMs ?? 400
        : optsRef.current.tickMs ?? 200;

      if (document.visibilityState === 'visible') {
        if (now - lastTick > tickMs) {
          lastTick = now;
          // Pointer cursor influence
          if (pointer) {
            const cx = Math.floor(pointer.x / cellSize);
            const cy = Math.floor(pointer.y / cellSize);
            for (let dy = -3; dy <= 3; dy++) {
              for (let dx = -3; dx <= 3; dx++) {
                if (Math.random() < 0.35) {
                  const x = cx + dx;
                  const y = cy + dy;
                  if (x >= 0 && y >= 0 && x < cols && y < rows) {
                    cells[y * cols + x] = 1;
                  }
                }
              }
            }
          }
          stepConway(cells, next, cols, rows);
          const tmp = cells;
          cells = next;
          next = tmp;
          // Anti-stagnation: every ~30s, inject pattern if too few live cells
          antiStagnationTimer += tickMs;
          if (antiStagnationTimer > 30_000) {
            antiStagnationTimer = 0;
            if (countLive(cells) < cols * rows * 0.02) {
              const cx = Math.floor(Math.random() * cols);
              const cy = Math.floor(Math.random() * rows);
              const choice = Math.floor(Math.random() * 3);
              if (choice === 0) stampPulsar(cells, cx, cy, cols, rows);
              else if (choice === 1) stampGlider(cells, cx, cy, cols, rows);
              else stampLWSS(cells, cx, cy, cols, rows);
            }
          }
          draw();
        }
      }
      rafId = requestAnimationFrame(loop);
    }

    function onMove(e: PointerEvent) {
      pointer = { x: e.clientX, y: e.clientY };
    }
    function onLeave() {
      pointer = null;
    }
    let touchHoldTimer: number | null = null;
    function onTouchStart(e: TouchEvent) {
      if (!e.touches.length) return;
      const t = e.touches[0];
      const cx = Math.floor(t.clientX / cellSize);
      const cy = Math.floor(t.clientY / cellSize);
      stampGlider(cells, cx, cy, cols, rows);
      touchHoldTimer = window.setTimeout(() => {
        stampGosperGun(cells, cx - 18, cy - 4, cols, rows);
      }, 600);
    }
    function onTouchEnd() {
      if (touchHoldTimer) {
        clearTimeout(touchHoldTimer);
        touchHoldTimer = null;
      }
    }

    window.addEventListener('resize', resize);
    canvas.addEventListener('pointermove', onMove);
    canvas.addEventListener('pointerleave', onLeave);
    canvas.addEventListener('touchstart', onTouchStart, { passive: true });
    canvas.addEventListener('touchend', onTouchEnd);

    resize();
    rafId = requestAnimationFrame(loop);

    return () => {
      stopped = true;
      cancelAnimationFrame(rafId);
      window.removeEventListener('resize', resize);
      canvas.removeEventListener('pointermove', onMove);
      canvas.removeEventListener('pointerleave', onLeave);
      canvas.removeEventListener('touchstart', onTouchStart);
      canvas.removeEventListener('touchend', onTouchEnd);
    };
  }, [canvasRef]);
}
